import React, { useState, useEffect, useCallback } from 'react';
import { Card, ProgressBar, Alert, Badge, Button, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, 
  faCheckCircle, 
  faExclamationTriangle,
  faClock,
  faFileImport,
  faRobot,
  faChartLine,
  faRedo,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';

interface ImportProgressTrackerProps {
  jobId: string;
  onComplete: (result: any) => void;
  onError: (error: string) => void;
}

interface ProgressData {
  status: 'queued' | 'processing' | 'completed' | 'failed';
  progress: number;
  processed: number;
  total: number;
  errors: string[];
  stage: string;
  eta_seconds?: number;
  created_leads?: number;
  failed_records?: number;
  start_time?: string;
  end_time?: string;
}

const STAGE_ICONS: Record<string, any> = {
  'queued': faSpinner,
  'downloading': faDownload,
  'parsing': faFileImport,
  'mapping': faRobot,
  'processing': faChartLine,
  'evaluating': faRobot,
  'completed': faCheckCircle,
  'failed': faExclamationTriangle
};

const STAGE_LABELS: Record<string, string> = {
  'queued': 'Waiting in Queue',
  'downloading': 'Downloading File',
  'parsing': 'Parsing Excel File',
  'mapping': 'Applying Field Mappings',
  'processing': 'Processing Records',
  'evaluating': 'Running AI Evaluation',
  'completed': 'Import Completed',
  'failed': 'Import Failed'
};

const ImportProgressTracker: React.FC<ImportProgressTrackerProps> = ({
  jobId,
  onComplete,
  onError
}) => {
  const [progressData, setProgressData] = useState<ProgressData | null>(null);
  const [isPolling, setIsPolling] = useState(true);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);

  const formatETA = (seconds?: number) => {
    if (!seconds) return 'Calculating...';
    if (seconds < 60) return `${seconds} seconds`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes`;
    return `${Math.floor(seconds / 3600)} hours`;
  };

  const calculateProcessingSpeed = () => {
    if (!progressData || !progressData.start_time || progressData.processed === 0) {
      return 'N/A';
    }
    const elapsed = (Date.now() - new Date(progressData.start_time).getTime()) / 1000 / 60; // minutes
    const speed = Math.round(progressData.processed / elapsed);
    return `${speed} records/min`;
  };

  const pollProgress = useCallback(async () => {
    if (!isPolling) return;

    try {
      const response = await axiosInstance.get(`/contact-import/progress/${jobId}`);
      const data: ProgressData = response.data;
      
      setProgressData(data);
      
      if (data.status === 'completed') {
        setIsPolling(false);
        onComplete({
          leads_created: data.created_leads || 0,
          failed_records: data.failed_records || 0,
          total_processed: data.processed,
          errors: data.errors
        });
      } else if (data.status === 'failed') {
        setIsPolling(false);
        setErrorDetails(data.errors || ['Unknown error occurred']);
        onError(data.errors?.[0] || 'Import failed');
      }
    } catch (error) {
      console.error('Progress polling error:', error);
      setIsPolling(false);
      onError('Failed to fetch progress');
    }
  }, [jobId, isPolling, onComplete, onError]);

  useEffect(() => {
    if (isPolling) {
      // Initial poll
      pollProgress();
      
      // Set up interval
      const interval = setInterval(pollProgress, 2000);
      return () => clearInterval(interval);
    }
  }, [isPolling, pollProgress]);

  const handleRetry = () => {
    setIsPolling(true);
    setErrorDetails([]);
  };

  if (!progressData) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="text-center py-5">
          <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary mb-3" />
          <h5>Initializing Import...</h5>
        </Card.Body>
      </Card>
    );
  }

  const progressVariant = progressData.status === 'completed' ? 'success' : 
                         progressData.status === 'failed' ? 'danger' : 'primary';

  return (
    <Card className="border-0 shadow-sm">
      <Card.Header className="bg-gradient-primary text-white">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <FontAwesomeIcon icon={faChartLine} className="me-2" />
            Import Progress
          </div>
          <Badge bg="light" text="dark">
            Job ID: {jobId.slice(0, 8)}...
          </Badge>
        </div>
      </Card.Header>
      
      <Card.Body>
        {/* Current Stage */}
        <div className="mb-4">
          <div className="d-flex align-items-center mb-2">
            <FontAwesomeIcon 
              icon={STAGE_ICONS[progressData.stage] || faSpinner} 
              className={`me-2 ${progressData.status === 'processing' ? 'fa-spin' : ''}`}
            />
            <h6 className="mb-0">{STAGE_LABELS[progressData.stage] || progressData.stage}</h6>
            {progressData.eta_seconds && progressData.status === 'processing' && (
              <Badge bg="info" className="ms-auto">
                <FontAwesomeIcon icon={faClock} className="me-1" />
                ETA: {formatETA(progressData.eta_seconds)}
              </Badge>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <ProgressBar 
            now={progressData.progress} 
            variant={progressVariant}
            animated={progressData.status === 'processing'}
            striped={progressData.status === 'processing'}
            label={`${progressData.progress}%`}
          />
          <div className="d-flex justify-content-between mt-2">
            <small className="text-muted">
              {progressData.processed} of {progressData.total} records
            </small>
            <small className="text-muted">
              Speed: {calculateProcessingSpeed()}
            </small>
          </div>
        </div>

        {/* Status Cards */}
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Card className="border-0 bg-light">
              <Card.Body className="text-center py-3">
                <h3 className="mb-0 text-primary">{progressData.processed}</h3>
                <small className="text-muted">Records Processed</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 bg-light">
              <Card.Body className="text-center py-3">
                <h3 className="mb-0 text-success">{progressData.created_leads || 0}</h3>
                <small className="text-muted">Leads Created</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 bg-light">
              <Card.Body className="text-center py-3">
                <h3 className="mb-0 text-warning">{progressData.failed_records || 0}</h3>
                <small className="text-muted">Failed Records</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Error Display */}
        {progressData.status === 'failed' && errorDetails.length > 0 && (
          <Alert variant="danger" className="mb-3">
            <Alert.Heading>
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              Import Failed
            </Alert.Heading>
            {errorDetails.map((error, idx) => (
              <div key={idx} className="small">• {error}</div>
            ))}
            <hr />
            <Button variant="outline-danger" size="sm" onClick={handleRetry}>
              <FontAwesomeIcon icon={faRedo} className="me-2" />
              Retry Import
            </Button>
          </Alert>
        )}

        {/* Completion Message */}
        {progressData.status === 'completed' && (
          <Alert variant="success" className="mb-0">
            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
            <strong>Import Completed Successfully!</strong>
            <div className="mt-2">
              {progressData.created_leads} leads have been created and queued for AI evaluation.
            </div>
          </Alert>
        )}

        {/* Warning for Failed Records */}
        {progressData.failed_records && progressData.failed_records > 0 && progressData.status === 'completed' && (
          <Alert variant="warning" className="mt-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {progressData.failed_records} records could not be imported. Check the error log for details.
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
};

export default ImportProgressTracker;